import type {
  DisasterTarget,
  DisasterTargetType,
  RefundExecuteRequest,
  RefundResultKind,
  RefundTargetResult,
  StopOperationResult,
  StopRegionType,
} from "~/types/admin-disaster";

// UL 確定語 + 機能名の直訳最小限（新規コピー創作をしない方針）
export const regionTypeLabels: Readonly<Record<StopRegionType, string>> = {
  postal_prefix: "郵便番号（前方一致）",
  prefecture: "都道府県",
};

export const targetTypeLabels: Readonly<
  Record<DisasterTarget["type"], string>
> = {
  cycle: "定期の回",
  one_time_order: "単品便",
};

export const refundResultLabels: Readonly<Record<RefundResultKind, string>> = {
  refunded: "返金済み",
  skipped: "スキップ",
  failed: "失敗",
  needs_review: "要確認",
  aborted: "中断",
};

export const refundResultOrder: readonly RefundResultKind[] = [
  "refunded",
  "skipped",
  "needs_review",
  "failed",
  "aborted",
];

export function targetKey(target: DisasterTarget): string {
  return `${target.type}:${target.id}`;
}

export function toggleTargetSelection(
  selected: readonly string[],
  key: string,
  checked: boolean,
): string[] {
  return checked
    ? [...new Set([...selected, key])]
    : selected.filter((value) => value !== key);
}

export function groupRefundResults(
  results: readonly RefundTargetResult[],
): { kind: RefundResultKind; label: string; results: RefundTargetResult[] }[] {
  return refundResultOrder
    .map((kind) => ({
      kind,
      label: refundResultLabels[kind],
      results: results.filter((result) => result.result === kind),
    }))
    .filter((group) => group.results.length > 0);
}

export function needsManualReconcile(
  results: readonly RefundTargetResult[],
): RefundTargetResult[] {
  return results.filter(
    (result) =>
      result.result === "skipped" && result.needs_manual_reconcile === true,
  );
}

// 返金実行は 1 回あたり最大 100 件（API 側上限のクライアント側強制）
export const REFUND_SELECTION_LIMIT = 100;

// 全選択は上限までに丸める（残りは分割実行）
export function selectAllRefundKeys(
  targets: readonly DisasterTarget[],
): string[] {
  return targets.slice(0, REFUND_SELECTION_LIMIT).map(targetKey);
}

// 停止操作のプレビュー時入力（確定はこの固定値から送る — 入力欄の現在値は使わない）
export interface StopOperationInput {
  event_id: string;
  region_type: StopRegionType;
  region_value: string;
}

// 停止操作の2段階（dry_run プレビュー → 再確認 → 確定）状態
export type StopOperationState =
  | { phase: "idle" }
  | { phase: "previewing" }
  | {
      phase: "ready";
      input: StopOperationInput;
      preview: StopOperationResult;
      confirmed: boolean;
    }
  | {
      phase: "executing";
      input: StopOperationInput;
      preview: StopOperationResult;
    }
  | {
      phase: "complete";
      preview: StopOperationResult;
      result: StopOperationResult;
    };

export type StopOperationAction =
  | { type: "reset" }
  | { type: "input_changed" }
  | { type: "preview_started" }
  | {
      type: "preview_received";
      input: StopOperationInput;
      preview: StopOperationResult;
    }
  | { type: "preview_failed" }
  | { type: "confirm_toggled"; confirmed: boolean }
  | { type: "execute_started" }
  | { type: "execute_received"; result: StopOperationResult }
  | { type: "execute_failed" };

export const initialStopOperationState: StopOperationState = { phase: "idle" };

export function stopOperationReducer(
  state: StopOperationState,
  action: StopOperationAction,
): StopOperationState {
  switch (action.type) {
    case "reset":
      return initialStopOperationState;
    case "input_changed":
      // 入力が変わったらプレビューと確認チェックを無効化する（確定不可に戻す）。
      // executing 中は固定値で確定が進行中のため状態を変えない。
      return state.phase === "executing" ? state : initialStopOperationState;
    case "preview_started":
      return { phase: "previewing" };
    case "preview_received":
      return {
        phase: "ready",
        input: action.input,
        preview: action.preview,
        confirmed: false,
      };
    case "preview_failed":
      return state.phase === "previewing" ? { phase: "idle" } : state;
    case "confirm_toggled":
      return state.phase === "ready"
        ? { ...state, confirmed: action.confirmed }
        : state;
    case "execute_started":
      return state.phase === "ready" && state.confirmed
        ? { phase: "executing", input: state.input, preview: state.preview }
        : state;
    case "execute_received":
      return state.phase === "executing"
        ? {
            phase: "complete",
            preview: state.preview,
            result: action.result,
          }
        : state;
    case "execute_failed":
      return state.phase === "executing"
        ? {
            phase: "ready",
            input: state.input,
            preview: state.preview,
            confirmed: false,
          }
        : state;
  }
}

export function canExecuteStopOperation(state: StopOperationState): boolean {
  return state.phase === "ready" && state.confirmed;
}

// 返金実行結果のトースト種別 — 1 件も返金されなかったら warning（成功と誤認させない）
export function refundResultToastKind(result: {
  refunded_count: number;
}): "success" | "warning" {
  return result.refunded_count > 0 ? "success" : "warning";
}

// A7: 任意対象の返金フォーム（理由必須・発送済み override は既定 false）
export interface ManualRefundForm {
  targetType: DisasterTargetType;
  targetId: string;
  reason: string;
  overrideShipmentGuard: boolean;
}

export const initialManualRefundForm: ManualRefundForm = {
  targetType: "cycle",
  targetId: "",
  reason: "",
  overrideShipmentGuard: false,
};

export type ManualRefundValidation =
  | { ok: true; request: RefundExecuteRequest }
  | { ok: false };

export function validateManualRefundForm(
  form: ManualRefundForm,
): ManualRefundValidation {
  const targetId = form.targetId.trim();
  const reason = form.reason.trim();
  if (!targetId || !reason) {
    return { ok: false };
  }
  return {
    ok: true,
    request: {
      targets: [{ type: form.targetType, id: targetId }],
      reason,
      override_shipment_guard: form.overrideShipmentGuard,
    },
  };
}
